import React, {useRef, useState} from 'react';
import * as XLSX from 'xlsx';
import {useMutation} from '@tanstack/react-query';
import AppButton from '../../../../../../../../components/common/AppButton';
import AppMenu from '../../../../../../../../components/common/AppMenu';
import {SVG} from '../../../../../../../../constants/assetLists';
import QUERY_KEYS from '../../../../../../../../constants/queryKeys';
import {useOnBoardingContext} from '../../../../../../../../context/onBoarding/OnBoardingContextProvider';
import {useSnackbar} from '../../../../../../../../context/SnackbarContextProvider';
import {handleCheckEmailsExistService} from '../../../../../../../../services/on-boarding';
import {
  DepartmentFormValues,
  OnboardingUserFormValues,
  RoleFormValues,
} from '../../../../types';

type ClientOption = {
  id: string;
  name: string;
  branches: {
    id: string;
    name: string;
    subBranches: {id: string; name: string}[];
  }[];
};

type Props = {
  onAdd: () => void;
  departments: DepartmentFormValues[];
  roles: RoleFormValues[];
  clients: ClientOption[];
  existingUsers: OnboardingUserFormValues[];
  onImportUsers: (users: Omit<OnboardingUserFormValues, 'id'>[]) => void;
};

const IMPORT_MENU_ITEMS = [
  {
    title: 'יבוא משתמשים מקובץ אקסל',
    value: 'import',
    icon: SVG.excelGreenIcon,
  },
];

// Hebrew column labels matching the AddEditUser form fields
const FIELD_LABELS: Record<string, string> = {
  firstName:       'שם פרטי',
  lastName:        'שם משפחה',
  email:           'אימייל',
  phone:           'טלפון',
  countryCode:     'קידומת מדינה',
  clientName:      'שם הארגון',
  branchName:      'שם הסניף',
  subBranchName:   'שם תת-הסניף',
  departmentName:  'שם המחלקה',
  roleName:        'שם התפקיד',
  sendPassword:    'שליחת סיסמה',
  sendPasswordVia: 'שליחת סיסמה באמצעות',
  birthday:        'תאריך לידה',
};

// Reverse map: Hebrew label → English field key (used when importing)
const LABEL_TO_FIELD: Record<string, string> = Object.fromEntries(
  Object.entries(FIELD_LABELS).map(([k, v]) => [v, k]),
);

export const TEMPLATE_ROW = {
  [FIELD_LABELS.firstName]:       'הדגמה',
  [FIELD_LABELS.lastName]:        'חשבון',
  [FIELD_LABELS.email]:           'demo@example.com',
  [FIELD_LABELS.phone]:           '0501234567',
  [FIELD_LABELS.countryCode]:     '972',
  [FIELD_LABELS.clientName]:      'שם הארגון',
  [FIELD_LABELS.branchName]:      'שם הסניף',
  [FIELD_LABELS.subBranchName]:   'שם תת-הסניף',
  [FIELD_LABELS.departmentName]:  'שם המחלקה',
  [FIELD_LABELS.roleName]:        'שם התפקיד',
  [FIELD_LABELS.sendPassword]:    'false',
  [FIELD_LABELS.sendPasswordVia]: 'email',
  [FIELD_LABELS.birthday]:        '1990-01-01',
};

export function downloadUsersTemplate() {
  const ws = XLSX.utils.json_to_sheet([TEMPLATE_ROW]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Users');
  XLSX.writeFile(wb, 'Users_Template.xlsx');
}

function parseSheet(ws: XLSX.WorkSheet) {
  const rows = XLSX.utils.sheet_to_json(ws, {header: 1, defval: ''}) as any[][];
  const nonEmptyRows = rows.filter(r => r.some(c => String(c).trim() !== ''));
  if (!nonEmptyRows.length) return [];
  const junkFirstRow = nonEmptyRows[0].every(
    (c, i) =>
      (i === 0 && String(c).trim().toLowerCase().includes('table')) ||
      String(c).trim() === '',
  );
  const headerRow = junkFirstRow ? (nonEmptyRows[1] ?? []) : nonEmptyRows[0];
  const dataRows = junkFirstRow ? nonEmptyRows.slice(2) : nonEmptyRows.slice(1);
  const rawHeaders = headerRow.map(h => String(h).trim());
  const keepIdx: number[] = [];
  const headers = rawHeaders
    .map((h, i) => {
      if (h) keepIdx.push(i);
      return h;
    })
    .filter(Boolean);
  return dataRows.map(r => {
    const obj: Record<string, any> = {};
    keepIdx.forEach((colIdx, j) => {
      obj[headers[j]] = r[colIdx] ?? '';
    });
    return obj;
  });
}

function AddUsers({onAdd, departments, roles, clients, existingUsers, onImportUsers}: Props) {
  const {allRoleTypes} = useOnBoardingContext();
  const {showSnackbar} = useSnackbar();
  const [importAnchorEl, setImportAnchorEl] = useState<HTMLElement | null>(null);
  const excelInputRef = useRef<HTMLInputElement>(null);

  const checkEmailsExistMutation = useMutation({
    mutationKey: [QUERY_KEYS.CHECK_EMAILS_EXIST],
    mutationFn: handleCheckEmailsExistService,
  });

  const handleMenuItemClick = (val: string) => {
    setImportAnchorEl(null);
    if (val === 'import') {
      excelInputRef.current?.click();
    } else if (val === 'download-template') {
      downloadUsersTemplate();
    }
  };

  const handleExcelPicked: React.ChangeEventHandler<HTMLInputElement> = e => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async evt => {
      try {
        const data = new Uint8Array(evt.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, {type: 'array'});
        const ws = workbook.Sheets[workbook.SheetNames[0]];
        const rawRows = parseSheet(ws);
        const rows = rawRows.map(row => {
          const normalised: Record<string, any> = {};
          for (const [k, v] of Object.entries(row)) {
            normalised[LABEL_TO_FIELD[k] ?? k] = v;
          }
          return normalised;
        });
        const validUsers: Omit<OnboardingUserFormValues, 'id'>[] = [];
        const seenEmails = new Set(
          existingUsers.map(u => u.email.trim().toLowerCase()),
        );
        const skipReasons: string[] = [];
        let rowNum = 0;

        for (const row of rows) {
          rowNum++;
          const firstName = String(row.firstName || '').trim();
          const lastName = String(row.lastName || '').trim();
          const email = String(row.email || '').trim().toLocaleLowerCase();
          const phone = String(row.phone || '').trim();

          if (!firstName || !lastName || !email || !phone) {
            skipReasons.push(`שורה ${rowNum}: חסרים שדות חובה (שם פרטי, שם משפחה, אימייל, טלפון)`);
            continue;
          }

          const userLabel = `${firstName} ${lastName}`;

          const emailKey = email;
          if (seenEmails.has(emailKey)) {
            skipReasons.push(`${userLabel}: אימייל כפול - ${email}`);
            continue;
          }
          seenEmails.add(emailKey);

          let clientIds: string[] = [];
          if (row.clientName?.toString().trim()) {
            const names = String(row.clientName)
              .split(',')
              .map((s: string) => s.trim())
              .filter(Boolean);
            const matched = names.map((n: string) => clients.find(c => c.name === n));
            if (matched.some(m => !m)) {
              skipReasons.push(`${userLabel}: ארגון לא נמצא - ${row.clientName}`);
              continue;
            }
            clientIds = matched.map(c => c!.id);
          }

          let branchIds: string[] = [];
          if (row.branchName?.toString().trim()) {
            const names = String(row.branchName)
              .split(',')
              .map((s: string) => s.trim())
              .filter(Boolean);
            const availBranches = clientIds.length
              ? clients.filter(c => clientIds.includes(c.id)).flatMap(c => c.branches)
              : clients.flatMap(c => c.branches);
            const matched = names.map((n: string) => availBranches.find(b => b.name === n));
            if (matched.some(m => !m)) {
              skipReasons.push(`${userLabel}: סניף לא נמצא - ${row.branchName}`);
              continue;
            }
            branchIds = matched.map(b => b!.id);
          }

          let subBranchIds: string[] = [];
          if (row.subBranchName?.toString().trim()) {
            const names = String(row.subBranchName)
              .split(',')
              .map((s: string) => s.trim())
              .filter(Boolean);
            const availSubBranches = branchIds.length
              ? clients
                  .filter(c => clientIds.includes(c.id))
                  .flatMap(c => c.branches)
                  .filter(b => branchIds.includes(b.id))
                  .flatMap(b => b.subBranches)
              : clients.flatMap(c => c.branches).flatMap(b => b.subBranches);
            const matched = names.map((n: string) => availSubBranches.find(sb => sb.name === n));
            if (matched.some(m => !m)) {
              skipReasons.push(`${userLabel}: תת-סניף לא נמצא - ${row.subBranchName}`);
              continue;
            }
            subBranchIds = matched.map(sb => sb!.id);
          }

          let departmentId: string[] | 'all' = [];
          if (row.departmentName?.toString().trim()) {
            const deptVal = String(row.departmentName).trim();
            if (deptVal.toLowerCase() === 'all' || deptVal === 'כל המחלקות') {
              departmentId = 'all';
            } else {
              const names = deptVal
                .split(',')
                .map((s: string) => s.trim())
                .filter(Boolean);
              const matched = names.map((n: string) => departments.find(d => d.name === n));
              if (matched.some(m => !m)) {
                skipReasons.push(`${userLabel}: מחלקה לא נמצאה - ${row.departmentName}`);
                continue;
              }
              departmentId = matched.map(d => d!.id);
            }
          }

          let roleId = '';
          if (row.roleName?.toString().trim()) {
            const roleTypeName = String(row.roleName).trim();
            const roleTypeEntry = allRoleTypes?.data?.find(
              rt => rt.name?.he === roleTypeName || rt.name?.en === roleTypeName,
            );
            if (!roleTypeEntry) {
              skipReasons.push(`${userLabel}: סוג תפקיד לא נמצא - ${row.roleName}`);
              continue;
            }
            const matchedRole = roles.find(r => r.roleType === roleTypeEntry._id);
            if (!matchedRole) {
              skipReasons.push(`${userLabel}: תפקיד לא נמצא בארגון/סניף - ${row.roleName}`);
              continue;
            }
            roleId = matchedRole.id;
          }

          validUsers.push({
            firstName,
            lastName,
            email,
            phone,
            countryCode: String(row.countryCode || '972').trim() || '972',
            clientIds,
            branchIds,
            subBranchIds,
            departmentId,
            roleId,
            sendPassword: ['email', 'sms'].includes(String(row.sendPasswordVia || '').trim().toLowerCase()),
            sendPasswordVia: String(row.sendPasswordVia || '').trim() || '',
            birthday: String(row.birthday || '').trim() || '',
            status: 'inactive' as const,
            userPermissions: [],
            notifyFromDepts: [],
            notifyQuickDepts: [],
            notifyTypes: [],
            enablePersonalNotifications: false,
          });
        }

        let finalValidUsers = validUsers;
        if (validUsers.length > 0) {
          try {
            const {existingEmails} = await checkEmailsExistMutation.mutateAsync(
              validUsers.map(u => u.email),
            );
            if (existingEmails.length > 0) {
              const existingSet = new Set(
                existingEmails.map(e => e),
              );
              finalValidUsers = validUsers.filter(u => {
                const exists = existingSet.has(u.email);
                if (exists) {
                  skipReasons.push(
                    `${u.firstName} ${u.lastName}: אימייל כבר קיים במערכת - ${u.email}`,
                  );
                }
                return !exists;
              });
            }
          } catch (checkErr) {
            console.warn('Email existence check failed, importing without it:', checkErr);
          }
        }

        const errorMessage =
          skipReasons.length > 0
            ? skipReasons.slice(0, 3).join('. ') +
              (skipReasons.length > 3
                ? ` ועוד ${skipReasons.length - 3} שגיאות`
                : '')
            : 'לא נמצאו משתמשים חוקיים בקובץ';

        if (finalValidUsers.length > 0) {
          onImportUsers(finalValidUsers);
          showSnackbar({
            message: `יובאו ${finalValidUsers.length} מתוך ${rowNum} משתמשים`,
            type: 'success',
          });
        }

        if (skipReasons.length > 0 || finalValidUsers.length === 0) {
          showSnackbar({
            message: errorMessage,
            type: 'error',
          });
          console.warn('Import skip reasons:', skipReasons);
        }
      } catch (err) {
        console.error('Error parsing xlsx:', err);
        showSnackbar({
          message: 'שגיאה בניתוח קובץ האקסל',
          type: 'error',
        });
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  return (
    <>
      <AppButton
        variant="primary"
        py="12px"
        iconStart={
          <div className="w-[12px] h-[12px]">
            <img
              src={SVG.addWhiteIcon}
              alt="icon"
              className="w-full h-full object-contain"
            />
          </div>
        }
        iconEnd={
          <AppMenu
            anchorEl={importAnchorEl}
            open={Boolean(importAnchorEl)}
            onOpen={e => setImportAnchorEl(e.currentTarget as HTMLElement)}
            onClose={() => setImportAnchorEl(null)}
            items={IMPORT_MENU_ITEMS}
            onItemClick={handleMenuItemClick}
            menuIcon={
              <div className="w-[12px] h-[6px]">
                <img
                  src={SVG.dropdownWhiteIcon}
                  alt="icon"
                  className="w-full h-full object-contain"
                />
              </div>
            }
          />
        }
        onClick={onAdd}>
        הוספה
      </AppButton>

      <input
        ref={excelInputRef}
        type="file"
        accept=".xlsx,.xls"
        className="hidden"
        onChange={handleExcelPicked}
      />
    </>
  );
}

export default AddUsers;
